<script>
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import Confirmation from '@/components/widgets/modals/Confirmation'
import { Helper } from '@/mixin/helper'
import { getToolsClient } from '@/services/tools'
import { getSmsClient } from '@/services/sms'
import { mapGetters } from 'vuex'
import * as encryptJwtApi from '@/services/rest-api-tools/lib/encrypt-jwt/encrypt-jwt'
import { sanitizeDecimal } from '@/helpers/parser'

const toolsAxios = getToolsClient()
const smsAxios = getSmsClient()

export default {
  components: { Confirmation },
  mixins: [Helper, validationMixin],
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    sensor: {
      type: Object,
      default: () => {},
    },
    vehicles: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    region: {
      type: String,
      default: 'SG'
    },
  },

  data() {
    return {
      activeTab: 0,
      form: {
        idleHigh: null,
        idleLow: null,
        alertLow: null,
        alertHigh: null,
        macAddress: null,
      },
      oldForm: {
        idleHigh: null,
        idleLow: null,
        alertLow: null,
        alertHigh: null,
      },
      calibrationForm: {
        temperature: 0,
        sensorType: null,
        currentTemp: 30,
        formula: 'x',
        calibrationEnabled: false,
        deviceTemp: 32,
        finalFormula: 'x',
      },
      calibrationHistory: [],
      sensorCode: 12,
      pinCode: null,
      sensorCodeList: [
        { id: 11, name: 'Temp 2' },
        { id: 12, name: 'Temp 1' },
        { id: 36, name: 'Temp 3' },
      ],
      modalConfirm: false,
      newData: null,
      oldData: null,
      columnData: null,
      sensors: [],
      diffTemperature: 0,
      latestTemperature: [],
      sensorTemperatureIndexMap: {
        12: 0,
        11: 1,
        36: 2,
      },
      ws: null,
    }
  },

  created() {
    const sensor_type = this.sensor?.sensor_type.find((v) => v.sensor_code === this.sensorCode)
    if (sensor_type) {
      this.pinCode = sensor_type.pin_code
      this.form.idleHigh = sensor_type.alert_idle_hi
      this.form.idleLow = sensor_type.alert_idle_lo
      this.form.alertLow = sensor_type.alert_lo
      this.form.alertHigh = sensor_type.alert_hi
      this.form.macAddress = sensor_type.mac_address

      this.oldForm.idleHigh = sensor_type.alert_idle_hi
      this.oldForm.idleLow = sensor_type.alert_idle_lo
      this.oldForm.alertLow = sensor_type.alert_lo
      this.oldForm.alertHigh = sensor_type.alert_hi

      this.calibrationForm.formula = sensor_type.status_text ?? '-'
      this.calibrationForm.finalFormula = sensor_type.status_text
    }
    this.getSensors()
    this.getCalibrationHistory()
    this.getTemperature()
  },

  validations: {
    form: {
      idleHigh: {
        required,
        minValue(val, { idleLow }) {
          return parseFloat(val) > parseFloat(idleLow) || 0
        },
      },
      idleLow: {
        required,
        maxValue(val, { idleHigh }) {
          return parseFloat(val) < parseFloat(idleHigh)
        },
      },
      alertLow: {
        required,
        maxValue(val, { alertHigh }) {
          return parseFloat(val) < parseFloat(alertHigh)
        },
      },
      alertHigh: {
        required,
        minValue(val, { alertLow }) {
          return parseFloat(val) > parseFloat(alertLow) || 0
        },
      },
    },
  },

  computed: {
    ...mapGetters({
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),

    showModal: {
      get() {
        return this.modal
      },
      set(newVal) {
        this.$emit('update:modal', newVal)
      },
    },
    loading: {
      get() {
        return this.isLoading
      },
      set(newVal) {
        this.$emit('update:isLoading', newVal)
      },
    },
    vehicleFields() {
      if (this.show_model && this.show_model.length > 0) {
        var result = this.show_model.map((a) => a.key)
        return this.vehicleField.filter(function (item) {
          return item.key == 'check' || result.includes(item.key)
        })
      } else {
        return this.vehicleField
      }
    },
    vehicleField() {
      return [
        {
          key: 'vehicle_status.last_packet',
          label: this.$t('vehicle.status.lastPacket'),
        },
        {
          key: 'vehicle_status.temperature',
          label: this.$t('vehicle.status.temperature'),
        },
        {
          key: 'vehicle_status.latitude',
          label: this.$t('vehicle.status.latitude'),
        },
        {
          key: 'vehicle_status.longitude',
          label: this.$t('vehicle.status.longitude'),
        },
        {
          key: 'vehicle_status.engine_on',
          label: this.$t('vehicle.status.ignition'),
        },
        {
          key: 'vehicle_status.speed',
          label: this.$t('vehicle.status.speed'),
        },
      ]
    },
    sensorFields() {
      if (this.show_model && this.show_model.length > 0) {
        var result = this.show_model.map((a) => a.key)
        return this.sensorField.filter(function (item) {
          return item.key == 'check' || result.includes(item.key)
        })
      } else {
        return this.sensorField
      }
    },
    sensorField() {
      return [
        {
          key: 'vehicle.imei',
          label: this.$t('task.column.imei'),
        },
        {
          key: 'vehicle.license_plate',
          label: this.$t('master.form.fleet.licensePlate'),
        },
        {
          key: 'alert_hi',
          label: this.$t('sensor.tempAlertHigh'),
        },
        {
          key: 'alert_lo',
          label: this.$t('sensor.tempAlertLow'),
        },
        {
          key: 'alert_idle_hi',
          label: this.$t('sensor.tempIdleHigh'),
        },
        {
          key: 'alert_idle_lo',
          label: this.$t('sensor.tempIdleLow'),
        },
        {
          key: 'edited_at',
          label: this.$t('sensor.editedAt'),
        },
        {
          key: 'edited_by',
          label: this.$t('sensor.editedBy'),
        },
      ]
    },
    isFormCalibrationActive() {
      return this.getFeatureFlagData('form_calibration_active').value
    },
    calibrationHistoryFields() {
      return [
        {
          key: 'imei',
          label: this.$t('task.column.imei'),
        },
        {
          key: 'license_plate',
          label: this.$t('master.form.fleet.licensePlate'),
        },
        {
          key: 'new_data',
          label: 'Formula',
        },
        {
          key: 'edit_on',
          label: this.$t('sensor.editedAt'),
        },
        {
          key: 'fullname',
          label: this.$t('sensor.editedBy'),
        },
      ]
    },
  },

  watch: {
    async sensorCode(newVal) {
      if (newVal) {
        const sensor_type = this.sensor.sensor_type.find((v) => v.sensor_code === newVal)
        if (sensor_type) {
          this.setValue(sensor_type)
          this.getSensors()
          this.getCalibrationHistory()
          this.resetCalibrationForm(sensor_type)
          await this.getTemperature()
        } else {
          this.form.idleHigh = null
          this.form.idleLow = null
          this.form.alertLow = null
          this.form.alertHigh = null
          this.form.macAddress = null
          this.pinCode = null
          this.sensors = []
          this.calibrationHistory = []
          this.resetCalibrationForm()
        }
      }
    },
  },
  beforeDestroy() {
    this.cleanupWebSocket()
  },
  methods: {
    preventInvalidChar(e) {
      const char = e.key;
      const currentValue = e.target.value;

      // Allow control keys (backspace, arrows, etc.) - keypress usually excludes these already
      if (!/[0-9.]/.test(char)) {
        e.preventDefault();
        return;
      }

      // Block a second dot
      if (char === '.' && currentValue.includes('.')) {
        e.preventDefault();
        return;
      }

      // Block typing more than 1 decimal digit
      const dotIndex = currentValue.indexOf('.');
      if (dotIndex !== -1) {
        const selectionStart = e.target.selectionStart;
        const decimalsSoFar = currentValue.slice(dotIndex + 1).length;
        // if cursor is after the dot and already has 1 decimal digit, block more
        if (selectionStart > dotIndex && decimalsSoFar >= 1) {
          e.preventDefault();
        }
      }
    },

    async handleCalculateFinalFormula(value, objName, fieldName, maxDecimals = 1) {
      const sanitized = sanitizeDecimal(value, maxDecimals);
      this[objName][fieldName] = sanitized;


      const { currentTemp, deviceTemp } = this.calibrationForm
      this.diffTemperature = Number((deviceTemp - currentTemp).toFixed(1))

      const vehicleId = this.vehicles[0]?.vehicle_id
      const payload = {
        current_value: Number(this.calibrationForm.currentTemp),
        expected_value: Number(this.calibrationForm.deviceTemp),
      }

      if (!vehicleId || !this.sensorCode || !this.pinCode) {
        this.$toast.error(this.$t('error.sensorNotAvailable'))
        return
      } 

      const response = await smsAxios
        .post(`/vehicle/${vehicleId}/calculate/${this.sensorCode}/${this.pinCode}`,{ ...payload }, {
          headers: {
            'X-Region': this.region,
          },
        })
        .catch((error) => {
          this.handleError(error)
        })

      if (!response?.data?.data) return

      this.calibrationForm.finalFormula = response.data.data
    },
    handleSubmit(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (this.activeTab === 0) {
        this.$v.form.$touch()
        if (this.$v.form.$error) {
          return
        }
      }
      
      if (!this.sensorCode || !this.pinCode) {
        this.$toast.error(this.$t('error.sensorNotAvailable'))
        return
      } 

      this.$v.form.$touch()
      this.modalConfirm = true
    },
    setValue(source) {
      this.pinCode = source.pin_code
      this.form.idleHigh = source.alert_idle_hi
      this.form.idleLow = source.alert_idle_lo
      this.form.alertLow = source.alert_lo
      this.form.alertHigh = source.alert_hi
      this.form.macAddress = source.mac_address
    },
    resetCalibrationForm(source = null) {
      const formula = source?.status_text ?? '-'
      this.calibrationForm.temperature = 0
      this.calibrationForm.currentTemp = 0
      this.calibrationForm.formula = formula
      this.calibrationForm.calibrationEnabled = false
      this.calibrationForm.deviceTemp = 0
      this.calibrationForm.finalFormula = source?.status_text || 'x'
      this.diffTemperature = 0
    },

    afterConfirm() {
      let pinCode = 0
      if (this.sensor.sensor_type) {
        const sensor_type = this.sensor?.sensor_type.find((v) => v.sensor_code === this.sensorCode)
        if (!sensor_type) this.$toast.error(this.$t('error.sensorNotAvailable'))
        pinCode = sensor_type.pin_code
      }

      if (this.diffTemperature > 2 || this.diffTemperature < -2) {
        return this.$toast.error("X cannot more than 2 and cannot less than -2")
      }

      const payload = {
        sensor_code: this.sensorCode,
        pin_code: pinCode,
        alert_idle_lo: parseFloat(this.form.idleLow),
        alert_idle_hi: parseFloat(this.form.idleHigh),
        alert_lo: parseFloat(this.form.alertLow),
        alert_hi: parseFloat(this.form.alertHigh),
        old_pin_code: this.pinCode,
        status_text: this.calibrationForm?.finalFormula
      }
      if (pinCode === 0) {
        this.$toast.error(this.$t('error.sensorNotAvailable'))
      } else {
        this.$emit('beforeSubmit', { payload, overwrite: true })
      }
    },
    handleError(error) {
      if (error.response) {
        if (error.response.status === 400) {
          if (error.response.data.details) {
            error.response.data.details.forEach((element) => {
              this.$toast.error(
                `${error.response.data.error} : ${element.field} ${element.message}`
              )
            })
          }
        } else if (error.response.status === 401) {
          this.$toast.error(error.response.data.error)
          localStorage.removeItem('token')
          localStorage.removeItem('tokenAccess')
          this.$router.push({ name: 'Login' })
        } else if (error.response.status === 403) {
          this.$toast.error(error.response.data.error)
        } else {
          this.$toast.error(error.response.data.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
      return
    },
    getVehicleId() {
      return this.vehicles[0]?.vehicle_id || this.vehicles[0]?.vehicleId
    },
    getSelectedTemperatureIndex() {
      return this.sensorTemperatureIndexMap[this.sensorCode] ?? 0
    },
    normalizeTemperatureValues(temperature) {
      if (Array.isArray(temperature)) return temperature
      return this.parseTemperatureString(temperature)
    },
    applySelectedTemperature(temperature) {
      const temperatureValues = this.normalizeTemperatureValues(temperature)
      this.latestTemperature = temperatureValues
      const selectedTemperature = temperatureValues[this.getSelectedTemperatureIndex()] ?? 0
      this.calibrationForm.currentTemp = selectedTemperature
      this.calibrationForm.temperature = selectedTemperature
    },
    applyTemperatureValue(temperature) {
      const normalizedTemperature = this.normalizeTemperatureValues(temperature)
      const selectedTemperature = Array.isArray(normalizedTemperature)
        ? normalizedTemperature[0] ?? 0
        : normalizedTemperature ?? 0
      this.calibrationForm.currentTemp = selectedTemperature
      this.calibrationForm.temperature = selectedTemperature
    },
    getRawSensorTemperature(row) {
      const fieldMap = {
        12: 'temperature',
        11: 'temperature2',
        36: 'temperature3',
      }
      const field = fieldMap[this.sensorCode] || 'temperature'
      const selectedTemperature = row?.[field]

      if (selectedTemperature !== undefined && selectedTemperature !== null) {
        return selectedTemperature
      }

      return this.normalizeTemperatureValues(row?.temperature)[this.getSelectedTemperatureIndex()]
    },
    parseTemperatureString(temperatureString) {
      if (temperatureString === null || temperatureString === undefined || temperatureString === '') {
        return ['-', '-', '-']
      }
      if (typeof temperatureString !== 'string') {
        return [temperatureString]
      }
      const normalizedTemperature = temperatureString.replace(' ', '')
      if (!normalizedTemperature.startsWith('[') || !normalizedTemperature.endsWith(']')) {
        return [temperatureString]
      }
      return normalizedTemperature
        .slice(1, -1)
        .split(',')
        .map((value) => (value === 'NULL' ? '-' : value))
    },
    cleanupWebSocket() {
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
    },
    async startTemperatureWebSocket() {
      const token = await this.getTemperatureWebSocketToken()
      if (token) this.startWsTemperature(token)
    },
    async getTemperatureWebSocketToken() {
      const vehicleId = this.getVehicleId()
      if (!vehicleId) return null
      try {
        const payload = {
          payload: {
            tags: [`gps-${vehicleId}`],
          },
        }
        const response = await encryptJwtApi.store(payload)
        return response.data
      } catch (error) {
        console.error('Gagal mendapatkan token temperature:', error)
        return null
      }
    },
    startWsTemperature(token) {
      this.cleanupWebSocket()

      let wsUrl = process.env.VUE_APP_WS_URL
      if (this.region === 'ID') {
        wsUrl = process.env.VUE_APP_WS_URL_ID
      }
      if (!wsUrl) return

      this.ws = new WebSocket(`${wsUrl}/ws?token=${localStorage.getItem('wsToken')}`)
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'update_tags',
          data: token,
        }))
      }
      this.ws.onmessage = (evt) => {
        const message = JSON.parse(evt.data)
        if (message?.type?.startsWith('gps-')) {
          const temperature = message.payload?.temperature
          if (temperature !== undefined && temperature !== null) {
            this.applySelectedTemperature(temperature)
          }
        }
      }
      this.ws.onclose = () => {
        console.log('WebSocket closed')
      }
    },
    getCalibrationHistoryRowPk() {
      const vehicleId = this.vehicles[0]?.vehicle_id || this.vehicles[0]?.vehicleId
      if (!vehicleId || !this.sensorCode || !this.pinCode) return null
      return `${vehicleId}|${this.sensorCode}|${this.pinCode}`
    },
    async getCalibrationHistory() {
      const rowPk = this.getCalibrationHistoryRowPk()
      if (!rowPk) {
        this.calibrationHistory = []
        return
      }

      const queryParameters = {
        page: 1,
        per_page: 10,
        filters: `auditLog:tableName:eq:sensor;auditLog:columnName:eq:status_text;auditLog:rowPk:eq:${rowPk}`,
        sorts: 'auditLog:editOn:desc',
        q: '',
        fields: '',
        includes: 'users:left:editId:userId'
      }
      const auditLogs = await smsAxios
        .get('/audit-log/vsms-log', {
          params: queryParameters,
          headers: {
            'X-Region': this.region,
          },
        })
        .catch((error) => {
          this.handleError(error)
        })

      if (!auditLogs?.data?.data) return

      const vehicle = this.vehicles[0]
      this.calibrationHistory = auditLogs?.data?.data?.map((log) => {
        return {
          ...log,
          edit_on: this.momentDMHms(log?.edit_on),
          license_plate: vehicle?.license_plate,
          imei: vehicle?.imei,
        }
      })
    },
    async getSensors() {
      const queryParameters = {
        includes: 'vehicle:left:vehicleId',
        filters: `vehicle:licensePlate:eq:${this.vehicles[0]?.license_plate};sensorType:sensorCode:eq:${this.sensorCode}`,
        page: 1,
        size: 1,
      }
      const sensors = await toolsAxios
        .get('/sensor', {
          params: queryParameters,
          headers: {
            'X-Region': this.region,
          },
        })
        .catch((error) => {
          this.handleError(error)
        })
      if (!sensors.data?.data) return
      this.sensors = sensors.data.data
    },
    async getTemperature() {
      const queryParameters = {
        filters: [`vehicle_id eq ${this.getVehicleId()}`],
        limit: 1,
        sorts: ['created_on desc'],
      }
      const rawSensorInfo = await smsAxios
        .get('/raw-sensor-info', {
          params: queryParameters,
          headers: {
            'X-Region': this.region,
          },
        })
        .catch((error) => {
          this.handleError(error)
        })
      if (!rawSensorInfo.data?.data?.paginated_result) return
      const currentTemp = rawSensorInfo.data?.data?.paginated_result
      this.applyTemperatureValue(this.getRawSensorTemperature(currentTemp[0]))
    }
  },
}
</script>
<template>
  <div>
    <b-modal
      v-model="showModal"
      class="mc-modal"
      id="modal-form"
      title="Temperature Setting"
      size="lg"
      centered
      :hide-header-close="loading"
      no-close-on-backdrop
      @hidden="cleanupWebSocket"
      @shown="startTemperatureWebSocket"
    >
      <div class="card-body mc-modal__filter p-2">
        <!-- Tabs -->
        <b-tabs v-model="activeTab" content-class="mt-3">
          <!-- Parameter Setting Tab -->
          <b-tab title="Parameter setting" active>
            <b-form @submit.stop.prevent="handleSubmit">
              <b-row>
                <b-col cols="6">
                  <label class="mt-3 required">{{ $t('sensor.type') }}</label>
                  <v-select
                    v-model="sensorCode"
                    :options="sensorCodeList"
                    placeholder="Pilih sensor"
                    label="name"
                    :reduce="(v) => v.id"
                  >
                  </v-select>
                </b-col>
                <b-col cols="6">
                  <label class="mt-3">{{ $t('sensor.macAddress') }}</label>
                  <b-form-input v-model="form.macAddress" type="text" disabled />
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="6">
                  <label class="mt-3 required">{{ $t('sensor.tempAlertLow') }}</label>
                  <b-form-input
                    v-model="form.alertLow"
                    type="number"
                    :placeholder="$t('sensor.tempAlertLow')"
                    :class="{
                      'is-invalid': $v.form.alertLow.$error,
                    }"
                  />
                  <div v-if="$v.form.alertLow.$error" class="invalid-feedback">
                    <span v-if="!$v.form.alertLow.required">{{ $t('sensor.required') }}</span>
                    <span v-else-if="!$v.form.alertLow.maxValue">
                      {{ $t('sensor.valueLessThan') + ' ' + $t('sensor.tempAlertHigh') }}
                    </span>
                  </div>
                </b-col>
                <b-col cols="6">
                  <label class="mt-3 required">{{ $t('sensor.tempIdleLow') }}</label>
                  <b-form-input
                    v-model="form.idleLow"
                    type="number"
                    :placeholder="$t('sensor.tempIdleLow')"
                    :class="{
                      'is-invalid': $v.form.idleLow.$error,
                    }"
                  />
                  <div v-if="$v.form.idleLow.$error" class="invalid-feedback">
                    <span v-if="!$v.form.idleLow.required">{{ $t('sensor.required') }}</span>
                    <span v-else-if="!$v.form.idleLow.maxValue">
                      {{ $t('sensor.valueLessThan') + ' ' + $t('sensor.tempIdleHigh') }}
                    </span>
                  </div>
                </b-col>
                <b-col cols="6">
                  <label class="mt-3 required">{{ $t('sensor.tempAlertHigh') }}</label>
                  <b-form-input
                    v-model="form.alertHigh"
                    type="number"
                    :placeholder="$t('sensor.tempAlertHigh')"
                    :class="{
                      'is-invalid': $v.form.alertHigh.$error,
                    }"
                  />
                  <div v-if="$v.form.alertHigh.$error" class="invalid-feedback">
                    <span v-if="!$v.form.alertHigh.required">{{ $t('sensor.required') }}</span>
                    <span v-else-if="!$v.form.alertHigh.minValue">
                      {{ $t('sensor.valueMoreThan') + ' ' + $t('sensor.tempAlertLow') }}
                    </span>
                  </div>
                </b-col>
                <b-col cols="6">
                  <label class="mt-3 required">{{ $t('sensor.tempIdleHigh') }}</label>
                  <b-form-input
                    v-model="form.idleHigh"
                    type="number"
                    :placeholder="$t('sensor.tempIdleHigh')"
                    :class="{
                      'is-invalid': $v.form.idleHigh.$error,
                    }"
                  />
                  <div v-if="$v.form.idleHigh.$error" class="invalid-feedback">
                    <span v-if="!$v.form.idleHigh.required">{{ $t('sensor.required') }}</span>
                    <span v-else-if="!$v.form.idleHigh.minValue">
                      {{ $t('sensor.valueMoreThan') + ' ' + $t('sensor.tempIdleLow') }}
                    </span>
                  </div>
                </b-col>
              </b-row>
            </b-form>
            <br />
            <h5>{{ $t('vehicle.status.lastCheckData') }}</h5>
            <div class="table-responsive mb-0 my-3 border border-1">
              <b-table
                class="datatables"
                :items="vehicles"
                :fields="vehicleFields"
                show-empty
                :empty-text="$t('global.emptyTable')"
                :busy="loading"
              >
                <template #cell(vehicle_status.temperature)="{ item }">
                  <div>
                    <p>
                      {{ $t('vehicle.status.temperature') }} 1:
                      {{ parseTemperatureString(item.vehicle_status.temperature)[0] }}
                    </p>
                    <p>
                      {{ $t('vehicle.status.temperature') }} 2:
                      {{ parseTemperatureString(item.vehicle_status.temperature)[1] }}
                    </p>
                    <p>
                      {{ $t('vehicle.status.temperature') }} 3:
                      {{ parseTemperatureString(item.vehicle_status.temperature)[2] }}
                    </p>
                  </div>
                </template>
                <template #cell(vehicle_status.last_packet)="data">
                  {{
                    data.item.vehicle_status
                      ? momentDMHms(data.item.vehicle_status.last_packet)
                      : '-'
                  }}
                </template>
                <template #cell(vehicle_status.engine_on)="data">
                  {{ data.item.vehicle_status ? '' : '-' }}
                  <span
                    class="mc-status mc-status--green"
                    v-if="data.item.vehicle_status.engine_on"
                  >
                    {{ $t('global.on') }}
                  </span>
                  <span class="mc-status mc-status--red" v-else>
                    {{ $t('global.off') }}
                  </span>
                </template>
              </b-table>
            </div>
            <br />
            <h5>{{ $t('vehicle.status.lastSetData') }}</h5>
            <div class="table-responsive mb-0 my-3 border border-1">
              <b-table
                class="datatables"
                :items="sensors"
                :fields="sensorFields"
                show-empty
                :empty-text="$t('global.emptyTable')"
                :busy="loading"
              >
                <template #cell(edited_at)="data">
                  {{ data.item.edited_at ? momentDMHms(data.item.edited_at) : '-' }}
                </template>
              </b-table>
            </div>
          </b-tab>

          <!-- Temperature Calibration Tab -->
          <b-tab v-if="isFormCalibrationActive" title="Temperature Calibration">
            <b-form>
              <b-row>
                <b-col cols="12">
                  <label class="mt-3 required">{{ $t('sensor.type') }}</label>
                  <v-select
                    v-model="sensorCode"
                    :options="sensorCodeList"
                    placeholder="Pilih sensor"
                    label="name"
                    :reduce="(v) => v.id"
                  >
                  </v-select>
                </b-col>
              </b-row>

              <b-row class="mt-3">
                <b-col cols="6">
                  <label>Suhu yang terbaca (°C) <span class="text-danger">*</span></label>
                  <b-form-input
                    v-model="calibrationForm.temperature"
                    type="text"
                    inputmode="decimal"
                    placeholder="30"
                    disabled
                  />
                </b-col>
                <b-col cols="6">
                  <label>Formula</label>
                  <b-form-input
                    v-model="calibrationForm.formula"
                    type="text"
                    placeholder="Y = x + 5"
                    disabled
                  />
                </b-col>
              </b-row>

              <b-row class="mt-3 align-items-center">
                <b-col cols="12" class="d-flex align-items-center">
                  <div class="form-check-right form-switch">
                    <input
                      id="calibration-enabled"
                      v-model="calibrationForm.calibrationEnabled"
                      class="form-check-input ms-2"
                      type="checkbox"
                    />
                    <label class="mb-0" for="calibration-enabled">Kalibrasi ulang?</label>
                  </div>
                </b-col>
              </b-row>

              <!-- Calibration Form (shown when toggle is ON) -->
              <div v-if="calibrationForm.calibrationEnabled">
                <b-row class="mt-3">
                  <b-col cols="6">
                    <label>Suhu Aplikasi saat ini (X) (°C)</label>
                    <b-input-group>
                      <b-form-input
                        v-model="calibrationForm.currentTemp"
                        type="text"
                        inputmode="decimal"
                        @keypress="preventInvalidChar($event)"
                        @input="handleCalculateFinalFormula($event, 'calibrationForm', 'currentTemp')"
                      />
                      <b-input-group-append>
                        <b-button
                          v-b-tooltip.hover.top="'Refresh suhu aplikasi'"
                          type="button"
                          variant="outline-secondary"
                          @click="getTemperature"
                        >
                          <i class="fas fa-sync-alt"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-col>
                  <b-col cols="6">
                    <label>Expected Sensor / Thermometer (A) (°C)</label>
                    <b-form-input
                      v-model="calibrationForm.deviceTemp"
                      type="text"
                      inputmode="decimal"
                      @keypress="preventInvalidChar($event)"
                      @input="handleCalculateFinalFormula($event, 'calibrationForm', 'deviceTemp')"
                    />
                  </b-col>
                </b-row>
              
              <b-row class="mt-3">
                <b-col cols="12">
                  <label>
                    Formula Used (Z) (°C)
                    <i
                      v-b-tooltip.hover.top="'Formula dihitung berdasarkan selisih antara Expected Sensor/Thermometer (A) dan suhu asli. Suhu asli diperoleh melalui reverse calculation dari formula yang sedang digunakan, kemudian sistem menghasilkan formula baru untuk proses kalibrasi.'"
                      class="fas fa-info-circle text-muted ms-1"
                    ></i>
                  </label>
                  <b-form-input
                    v-model="calibrationForm.finalFormula"
                    placeholder="x - 2"
                    type="text"
                    inputmode="decimal"
                    disabled
                  />
                </b-col>
              </b-row>

                <b-row class="mt-3">
                  <b-col cols="12">
                    <h6>Terakhir Mengatur Data</h6>
                    <div class="table-responsive mb-0 my-3 border border-1">
                      <b-table
                        class="datatables"
                        :items="calibrationHistory"
                        :fields="calibrationHistoryFields"
                        show-empty
                        empty-text="Tidak ada data"
                        :busy="loading"
                      >
                      </b-table>
                    </div>
                  </b-col>
                </b-row>

              </div>
            </b-form>
          </b-tab>
        </b-tabs>
      </div>
      <template #modal-footer="{ cancel }">
        <b-row class="mc-modal__filter-footer">
          <b-col cols="12" class="mc-modal__filter-footer-btn">
            <b-button variant="outline-primary" @click="cancel()" :disabled="loading" class="me-3">
              Batal
            </b-button>
            <b-button @click="handleSubmit" variant="primary" :disabled="loading">
              <b-spinner v-if="loading" small type="grow"></b-spinner>
              {{ loading ? 'Loading...' : 'Simpan' }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
    <confirmation
      v-if="modalConfirm"
      :modal.sync="modalConfirm"
      message="Yakin ingin melakukan Pengaturan Parameter"
      @confirmed="afterConfirm"
    />
  </div>
</template>
